import { useState, useEffect, useRef } from "react";
import { auth, signInWithGoogle, signUpWithEmail, signInWithEmail, sendPasswordReset, resendVerificationEmail } from "./firebase";
import { useTheme } from "./theme";
import { GLOBAL_CSS } from "./styles";

function emailAuthError(code) {
  switch (code) {
    case "auth/email-already-in-use":    return "An account with this email already exists. Try signing in.";
    case "auth/invalid-email":           return "Please enter a valid email address.";
    case "auth/weak-password":           return "Password must be at least 6 characters.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":      return "Incorrect email or password.";
    case "auth/too-many-requests":       return "Too many attempts. Please try again later.";
    case "auth/network-request-failed":  return "Network error. Please check your connection.";
    case "auth/account-exists-with-different-credential":
      return "This email is linked to a Google account. Please sign in with Google.";
    default: return "Something went wrong. Please try again.";
  }
}

// A ?email= param on arrival (e.g. from the teacher-partner pitch page's post-submit "Sign up/
// Login to Speafy" button, which knows the address from its own form) prefills the email field
// and jumps straight to the email method/signup mode — skipping "Continue with Google" and the
// empty-field click-through for someone who has already told us their email once. Read once at
// mount, not on every render: nothing here needs to react to a later history change.
function emailParamFromLocation() {
  try {
    return new URLSearchParams(window.location.search).get("email") || "";
  } catch {
    return "";
  }
}

export function SignInScreen() {
  const [error, setError]               = useState("");
  const [loading, setLoading]           = useState(false);
  const [prefillEmail]                  = useState(emailParamFromLocation);
  const [method, setMethod]             = useState(() => (prefillEmail ? "email" : "google")); // "google" | "email"
  const [emailMode, setEmailMode]       = useState(() => (prefillEmail ? "signup" : "signin")); // "signin" | "signup"
  const [forgotMode, setForgotMode]     = useState(false);
  const [forgotSent, setForgotSent]     = useState(false);
  const [email, setEmail]               = useState(prefillEmail);
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { darkMode } = useTheme();
  const dk = darkMode;

  // "One app. Every tool." animates once, when it first scrolls into view (on phones the
  // left panel sits below the form, so a load-time animation would finish unseen).
  const toolsHeadingRef = useRef(null);
  const [toolsInView, setToolsInView] = useState(false);
  useEffect(() => {
    const el = toolsHeadingRef.current;
    if (!el || typeof IntersectionObserver === "undefined") { setToolsInView(true); return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setToolsInView(true); io.disconnect(); }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const rp = {
    bg: dk?"#12080A":"#FDF6EE", h2: dk?"#E07070":"#6B1515",
    sub: dk?"#9A7060":"#8B7355", gbg: dk?"#1C1010":"#fff",
    gcol: dk?"#F0E0D0":"#2C1810", gbdr: dk?"#4A2828":"#E8D5B7",
    trial: dk?"#9A7060":"#B0956A",
    input: dk?"#1C1010":"#fff", inputBdr: dk?"#4A2828":"#E8D5B7",
    inputCol: dk?"#F0E0D0":"#2C1810", inputPh: dk?"#6A4040":"#B0956A",
    divCol: dk?"#3A2020":"#E8D5B7", divText: dk?"#6A4040":"#B0956A",
    link: dk?"#E07070":"#7B1E1E",
  };

  const switchMethod = (m) => { setMethod(m); setError(""); setForgotMode(false); setForgotSent(false); };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try { await signInWithGoogle(); }
    catch (e) { console.error("Sign-in error:", e.code, e.message); setError("Sign-in failed. Please try again."); setLoading(false); }
  };

  const handleEmailSubmit = async () => {
    if (!email.trim() || !password) { setError("Please enter your email and password."); return; }
    setError(""); setLoading(true);
    try {
      if (emailMode === "signup") {
        await signUpWithEmail(email.trim(), password);
        await resendVerificationEmail();
        // onAuthStateChanged fires → screen changes to "verify-email"
      } else {
        await signInWithEmail(email.trim(), password);
        // onAuthStateChanged fires → screen changes to dashboard/onboarding
      }
    } catch (e) {
      setError(emailAuthError(e.code));
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError("Enter your email address above first."); return; }
    setError(""); setLoading(true);
    try {
      await sendPasswordReset(email.trim());
      setForgotSent(true);
    } catch (e) {
      setError(emailAuthError(e.code));
    }
    setLoading(false);
  };

  const inputStyle = {
    width:"100%", padding:"12px 14px", boxSizing:"border-box",
    background:rp.input, border:`1.5px solid ${rp.inputBdr}`, borderRadius:10,
    fontFamily:"'Nunito',sans-serif", fontSize:"1rem", color:rp.inputCol,
    outline:"none", transition:"border-color 0.2s",
  };

  // Mirrors the "One app. Every tool." section on speafy.com (landing/index.html).
  const features = [
    { img: "/babi%20fantasy2.jpg?v=2", round: true, title: "AI tutor, Babi",
      desc: "Ask anything, any time. Babi answers your language questions in your own language." },
    { img: "/flashcard_icon.png", title: "Smart flashcards",
      desc: "FSRS-powered reviews that adapt to your memory. Always the right word at the right time." },
    { icon: "🔊", title: "Native-quality audio",
      desc: "Every word and example sentence is recorded with natural, native-quality voices for an authentic listening experience." },
    { icon: "📈", title: "Progress analytics",
      desc: "Track your streak, vocabulary growth, and recall accuracy with clear visual charts." },
    { icon: "🌍", title: "Multi-language UI",
      desc: "Use Speafy in your own language — the app is fully built in 6 major base languages." },
    { icon: "💬", title: "Practice dialogues",
      desc: "AI-generated conversations using your current lesson's vocabulary. Real practice, not drills." },
  ];

  return (
    <>
      <style>{`
        .signin-mobile-logo { display: none; }
        @media (max-width: 48em) {
          .signin-wrap { flex-direction: column; }
          .signin-right { flex: none !important; padding: 36px 24px 40px !important; }
          .signin-left { flex: none !important; order: 2; padding: 44px 24px 48px !important; }
          .signin-mobile-logo { display: block; margin: 0 auto 20px; }
        }
        @media (max-width: 30em) { .signin-features { grid-template-columns: 1fr !important; } }
        .signin-tools-part { display: inline-block; opacity: 0; transform: translateY(14px); }
        .signin-tools.in .signin-tools-part { animation: signinSlideUp 0.6s cubic-bezier(.2,.7,.3,1) forwards; }
        .signin-tools.in .signin-tools-gold { animation: signinSlideUp 0.6s cubic-bezier(.2,.7,.3,1) 0.3s forwards, signinShine 1.3s ease-in-out 1s forwards; }
        .signin-tools-gold {
          background: linear-gradient(100deg, #D9A441 0%, #F0C75E 38%, #FFF4D6 50%, #F0C75E 62%, #D9A441 100%);
          background-size: 250% 100%; background-position: 100% 0;
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        @keyframes signinSlideUp { to { opacity: 1; transform: none; } }
        @keyframes signinShine { from { background-position: 100% 0; } to { background-position: 0 0; } }
        @media (prefers-reduced-motion: reduce) {
          .signin-tools-part, .signin-tools.in .signin-tools-part, .signin-tools.in .signin-tools-gold { opacity: 1; transform: none; animation: none; }
        }
        .signin-google-btn:hover:not(:disabled) { border-color: #C9952A !important; box-shadow: 0 4px 16px rgba(0,0,0,0.1) !important; }
        .signin-email-input:focus { border-color: #7B1E1E !important; }
        .signin-link-btn { background:none; border:none; padding:0; cursor:pointer; font-family:'Nunito',sans-serif; font-size:0.8125rem; font-weight:700; text-decoration:underline; }
        .signin-link-btn:hover { opacity:0.75; }
        .signin-submit-btn:hover:not(:disabled) { background: linear-gradient(135deg,#6B1515,#A33030) !important; }
        .signin-gold-btn:hover { background: linear-gradient(135deg,#B8851F,#C9952A) !important; }
        .signin-more-btn:hover { background: linear-gradient(135deg,#6B1515,#A33030) !important; }
      `}</style>
      <div className="signin-wrap" style={{display:"flex",minHeight:"100vh",fontFamily:"'Nunito',sans-serif"}}>

        {/* ── LEFT PANEL ── */}
        <div className="signin-left" style={{
          flex:"1.1", background:"linear-gradient(150deg,#6B1515 0%,#8B2020 55%,#7B1E1E 100%)",
          padding:"60px 52px", display:"flex", flexDirection:"column", justifyContent:"center",
          position:"relative", overflow:"hidden",
        }}>
          <span style={{position:"absolute",right:"5%",bottom:"6%",fontSize:"8.125rem",opacity:0.06,lineHeight:1,pointerEvents:"none",userSelect:"none"}}>🇹🇷</span>

          <div style={{marginBottom:32}}>
            <img src="/speafy-logo-rectangular-bg-removed-200x50.png" alt="Speafy" style={{height:42}} />
          </div>

          <p style={{fontFamily:"'Playfair Display',serif",fontSize:"1.5rem",fontWeight:600,color:"#fff",lineHeight:1.45,marginBottom:36,maxWidth:480}}>
            Speafy pairs an AI tutor with science-backed spaced repetition — so every word you learn actually sticks.
          </p>

          <div style={{fontSize:"0.6875rem",fontWeight:700,letterSpacing:"2px",textTransform:"uppercase",color:"#E3B85C",marginBottom:6}}>Everything you need</div>
          <h3 ref={toolsHeadingRef} className={"signin-tools" + (toolsInView ? " in" : "")} style={{fontFamily:"'Playfair Display',serif",fontSize:"2rem",fontWeight:700,color:"#fff",lineHeight:1.2,marginBottom:20}}>
            <span className="signin-tools-part">One app.</span>{" "}
            <span className="signin-tools-part signin-tools-gold">Every tool.</span>
          </h3>
          <div className="signin-features" style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0, 1fr))",gap:12,maxWidth:540,position:"relative"}}>
            {features.map(f => (
              <div key={f.title} style={{
                background:"#fff", border:"1px solid #F0DFC0",
                borderRadius:14, padding:"14px 16px", boxShadow:"0 4px 16px rgba(0,0,0,0.12)",
              }}>
                <div style={{height:30,marginBottom:8,display:"flex",alignItems:"center"}}>
                  {f.img
                    ? <img src={f.img} alt="" style={{width:30,height:30,objectFit:f.round?"cover":"contain",borderRadius:f.round?"50%":6}} />
                    : <span aria-hidden="true" style={{fontSize:"1.375rem",lineHeight:1}}>{f.icon}</span>}
                </div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:"0.875rem",fontWeight:700,color:"#2C1810",marginBottom:4}}>{f.title}</div>
                <div style={{fontSize:"0.75rem",color:"#5C3D2E",lineHeight:1.5}}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="signin-right" style={{
          flex:"0.9", background:rp.bg, display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center", padding:"60px 48px", minWidth:0,
          transition:"background .25s",
        }}>
          <div style={{width:"100%",maxWidth:300}}>

            <img className="signin-mobile-logo" src="/speafy-logo-rectangular-bg-removed-200x50.png" alt="Speafy" style={{height:36,filter:dk?"brightness(0) invert(1)":"none"}} />

            {/* Babi avatar */}
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:24}}>
              <div style={{width:72,height:72,borderRadius:"50%",overflow:"hidden",marginBottom:8,boxShadow:"0 4px 24px rgba(123,30,30,0.18)"}}>
                <img src="/babi%20fantasy2.jpg?v=2" alt="Babi, your AI tutor" style={{width:"100%",height:"100%",objectFit:"cover"}} />
              </div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1rem",fontWeight:700,color:rp.gcol}}>Babi</div>
              <div style={{fontSize:"0.75rem",color:rp.sub,marginTop:2}}>Your AI tutor</div>
            </div>

            <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.75rem",fontWeight:700,color:rp.h2,textAlign:"center",lineHeight:1.2,marginBottom:10}}>
              Learn Turkish,<br/>the way your brain wants
            </h1>
            <p style={{fontSize:"0.8125rem",color:rp.sub,textAlign:"center",lineHeight:1.6,marginBottom:24}}>
              Sign in to start or continue your learning journey.
            </p>

            {/* ── GOOGLE METHOD ── */}
            {method === "google" && (
              <>
                <button
                  className="signin-google-btn"
                  onClick={handleGoogle}
                  disabled={loading}
                  style={{
                    display:"flex", alignItems:"center", gap:10, justifyContent:"center",
                    width:"100%", padding:"13px 20px",
                    background:rp.gbg, border:`1.5px solid ${rp.gbdr}`, borderRadius:12,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700, color:rp.gcol,
                    boxShadow:"0 2px 8px rgba(0,0,0,0.06)", transition:"border-color 0.2s, box-shadow 0.2s",
                    opacity: loading ? 0.45 : 1,
                  }}>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" style={{width:20,height:20,background:"#fff",borderRadius:2,padding:2}} />
                  {loading ? "Signing in…" : "Continue with Google"}
                </button>

                {error && <p style={{color:"#c0392b",fontSize:"0.8125rem",marginTop:10,textAlign:"center"}}>{error}</p>}

                {/* Divider */}
                <div style={{display:"flex",alignItems:"center",gap:10,margin:"18px 0"}}>
                  <div style={{flex:1,height:1,background:rp.divCol}} />
                  <span style={{fontSize:"0.75rem",color:rp.divText,whiteSpace:"nowrap"}}>or</span>
                  <div style={{flex:1,height:1,background:rp.divCol}} />
                </div>

                <button
                  className="signin-gold-btn"
                  onClick={() => switchMethod("email")}
                  style={{
                    width:"100%", padding:"13px 20px",
                    background:"linear-gradient(135deg,#C9952A,#D9AB45)", border:"none", borderRadius:12,
                    cursor:"pointer", fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700, color:"#2C1810",
                    boxShadow:"0 2px 8px rgba(201,149,42,0.25)", transition:"background 0.2s",
                  }}>
                  Continue with Email
                </button>

                <p style={{fontSize:"0.8125rem",color:rp.sub,textAlign:"center",marginTop:14}}>
                  New here?{" "}
                  <button className="signin-link-btn" onClick={() => { switchMethod("email"); setEmailMode("signup"); }} style={{color:rp.link}}>
                    Create an account
                  </button>
                </p>
              </>
            )}

            {/* ── EMAIL METHOD ── */}
            {method === "email" && (
              <>
                {forgotSent ? (
                  <div style={{background:dk?"#1A1010":"#F0FDF4",border:"1px solid #6EE7B7",borderRadius:12,padding:"16px 18px",textAlign:"center",marginBottom:16}}>
                    <div style={{fontSize:"1.25rem",marginBottom:6}}>✉</div>
                    <div style={{fontWeight:700,color:"#065F46",marginBottom:4}}>Check your inbox</div>
                    <div style={{fontSize:"0.8125rem",color:"#047857"}}>We sent a password reset link to <strong>{email}</strong></div>
                  </div>
                ) : (
                  <>
                    {/* Email field */}
                    <div style={{marginBottom:10}}>
                      <input
                        className="signin-email-input"
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={e => { setEmail(e.target.value); setError(""); }}
                        onKeyDown={e => e.key === "Enter" && !forgotMode && handleEmailSubmit()}
                        disabled={loading}
                        style={inputStyle}
                        autoComplete="email"
                      />
                    </div>

                    {/* Password field (hidden in forgot mode) */}
                    {!forgotMode && (
                      <div style={{position:"relative",marginBottom:6}}>
                        <input
                          className="signin-email-input"
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          value={password}
                          onChange={e => { setPassword(e.target.value); setError(""); }}
                          onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
                          disabled={loading}
                          style={{...inputStyle, paddingRight:44}}
                          autoComplete={emailMode === "signup" ? "new-password" : "current-password"}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          style={{
                            position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
                            background:"none", border:"none", cursor:"pointer", padding:0,
                            color:rp.sub, fontSize:"0.8125rem", lineHeight:1,
                          }}>
                          {showPassword ? "Hide" : "Show"}
                        </button>
                      </div>
                    )}

                    {/* Forgot password link (sign-in mode only) */}
                    {!forgotMode && emailMode === "signin" && (
                      <div style={{textAlign:"right",marginBottom:14}}>
                        <button className="signin-link-btn" onClick={() => { setForgotMode(true); setError(""); }} style={{color:rp.link}}>
                          Forgot password?
                        </button>
                      </div>
                    )}
                    {!forgotMode && emailMode === "signup" && (
                      <div style={{marginBottom:14}} />
                    )}

                    {error && <p style={{color:"#c0392b",fontSize:"0.8125rem",marginTop:0,marginBottom:10,textAlign:"center"}}>{error}</p>}

                    {/* Submit button */}
                    {!forgotMode && (
                      <button
                        className="signin-submit-btn"
                        onClick={handleEmailSubmit}
                        disabled={loading}
                        style={{
                          width:"100%", padding:"13px 20px",
                          background:"linear-gradient(135deg,#7B1E1E,#A33030)", color:"#fff",
                          border:"none", borderRadius:12, cursor: loading ? "not-allowed" : "pointer",
                          fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
                          transition:"background 0.2s", opacity: loading ? 0.6 : 1,
                        }}>
                        {loading ? (emailMode === "signup" ? "Creating account…" : "Signing in…")
                                 : (emailMode === "signup" ? "Create Account" : "Sign In")}
                      </button>
                    )}

                    {/* Forgot password send button */}
                    {forgotMode && (
                      <>
                        {error && <p style={{color:"#c0392b",fontSize:"0.8125rem",marginBottom:10,textAlign:"center"}}>{error}</p>}
                        <button
                          className="signin-submit-btn"
                          onClick={handleForgotPassword}
                          disabled={loading}
                          style={{
                            width:"100%", padding:"13px 20px",
                            background:"linear-gradient(135deg,#7B1E1E,#A33030)", color:"#fff",
                            border:"none", borderRadius:12, cursor: loading ? "not-allowed" : "pointer",
                            fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
                            opacity: loading ? 0.6 : 1,
                          }}>
                          {loading ? "Sending…" : "Send Reset Link"}
                        </button>
                        <button className="signin-link-btn" onClick={() => { setForgotMode(false); setError(""); }} style={{color:rp.sub,marginTop:10,display:"block",marginLeft:"auto",marginRight:"auto"}}>
                          Back to sign in
                        </button>
                      </>
                    )}

                    {/* Sign in / Create account toggle */}
                    {!forgotMode && (
                      <p style={{fontSize:"0.8125rem",color:rp.sub,textAlign:"center",marginTop:14}}>
                        {emailMode === "signin" ? "Don't have an account? " : "Already have an account? "}
                        <button className="signin-link-btn" onClick={() => { setEmailMode(emailMode === "signin" ? "signup" : "signin"); setError(""); setPassword(""); }} style={{color:rp.link}}>
                          {emailMode === "signin" ? "Create one" : "Sign in"}
                        </button>
                      </p>
                    )}
                  </>
                )}

                {/* Back to Google */}
                <div style={{textAlign:"center",marginTop:16}}>
                  <button className="signin-link-btn" onClick={() => switchMethod("google")} style={{color:rp.sub}}>
                    ← Use Google instead
                  </button>
                </div>
              </>
            )}

            <p style={{fontSize:"0.75rem",color:rp.trial,textAlign:"center",marginTop:20,lineHeight:1.7}}>
              ✓ No credit card &nbsp;·&nbsp; ✓ 7-day free trial &nbsp;·&nbsp; ✓ Cancel anytime
            </p>
            <a
              className="signin-more-btn"
              href="https://speafy.com"
              style={{
                display:"block", boxSizing:"border-box", width:"100%", padding:"13px 20px", marginTop:14,
                background:"linear-gradient(135deg,#7B1E1E,#A33030)", color:"#fff", textAlign:"center", textDecoration:"none",
                borderRadius:12, fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
                transition:"background 0.2s",
              }}>
              I need to know more
            </a>
          </div>
        </div>

      </div>
    </>
  );
}

export function VerifyEmailScreen() {
  const [checking, setChecking] = useState(false);
  const [resent,   setResent]   = useState(false);
  const [error,    setError]    = useState("");
  const { darkMode } = useTheme();
  const dk = darkMode;
  const email = auth?.currentUser?.email || "";

  const handleResend = async () => {
    setError(""); setResent(false);
    try {
      await resendVerificationEmail();
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch {
      setError("Could not resend. Please try again in a moment.");
    }
  };

  const handleCheck = async () => {
    setChecking(true); setError("");
    try {
      await auth?.currentUser?.reload();
      if (auth?.currentUser?.emailVerified) {
        window.location.reload();
      } else {
        setError("Not verified yet — please click the link in your email, then try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
    setChecking(false);
  };

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{
        minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
        background: dk ? "#12080A" : "#FDF6EE", fontFamily:"'Nunito',sans-serif", padding:"40px 20px",
      }}>
        <div style={{
          width:"100%", maxWidth:420,
          background: dk ? "#1F1212" : "#fff",
          border:`1px solid ${dk?"#3A2020":"#F0DFC0"}`,
          borderRadius:22, padding:"44px 40px", textAlign:"center",
          boxShadow:"0 8px 40px rgba(123,30,30,0.10)",
        }}>
          <div style={{fontSize:"3rem",marginBottom:20}}>✉</div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.625rem",fontWeight:700,color:dk?"#E07070":"#6B1515",marginBottom:12}}>
            Verify your email
          </h2>
          <p style={{fontSize:"0.9375rem",color:dk?"#9A7060":"#5C3D2E",lineHeight:1.7,marginBottom:8}}>
            We sent a verification link to:
          </p>
          <p style={{fontWeight:700,fontSize:"0.9375rem",color:dk?"#F0E0D0":"#2C1810",marginBottom:28,wordBreak:"break-all"}}>
            {email}
          </p>
          <p style={{fontSize:"0.875rem",color:dk?"#8A6060":"#8B7355",lineHeight:1.7,marginBottom:32}}>
            Click the link in that email, then come back here and press the button below.
          </p>

          <button
            onClick={handleCheck}
            disabled={checking}
            style={{
              width:"100%", padding:"13px 20px", marginBottom:12,
              background:"linear-gradient(135deg,#7B1E1E,#A33030)", color:"#fff",
              border:"none", borderRadius:12, cursor: checking ? "not-allowed" : "pointer",
              fontFamily:"'Nunito',sans-serif", fontSize:"0.9375rem", fontWeight:700,
              opacity: checking ? 0.6 : 1,
            }}>
            {checking ? "Checking…" : "I've verified my email →"}
          </button>

          {error && <p style={{color:"#c0392b",fontSize:"0.8125rem",marginBottom:12}}>{error}</p>}

          <p style={{fontSize:"0.8125rem",color:dk?"#8A6060":"#8B7355",marginBottom:4}}>
            Didn't receive it?{" "}
            <button
              onClick={handleResend}
              style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Nunito',sans-serif",fontSize:"0.8125rem",fontWeight:700,color:dk?"#E07070":"#7B1E1E",textDecoration:"underline",padding:0}}>
              {resent ? "Sent ✓" : "Resend email"}
            </button>
          </p>
          <p style={{fontSize:"0.8125rem",color:dk?"#8A6060":"#8B7355"}}>
            Wrong address?{" "}
            <button
              onClick={() => { auth.signOut(); }}
              style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Nunito',sans-serif",fontSize:"0.8125rem",fontWeight:700,color:dk?"#E07070":"#7B1E1E",textDecoration:"underline",padding:0}}>
              Sign out
            </button>
          </p>
        </div>
      </div>
    </>
  );
}
